import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, DocumentTextIcon, CurrencyDollarIcon, ArchiveBoxIcon, ChartBarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, ChartPieIcon, ArrowPathIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import { useDocuments } from '../../contexts/DocumentsContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import stockService from '../../services/stockService';
import inventoryService from '../../services/inventoryService';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const InventoryReports = ({ onBack, documents, products, customers, suppliers }) => {
  const [selectedReport, setSelectedReport] = useState('stock-overview');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [isGenerating, setIsGenerating] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [stockMovementsData, setStockMovementsData] = useState([]);
  const [lowStockData, setLowStockData] = useState([]);
  const [stockValuationData, setStockValuationData] = useState([]);
  const [stockTrends, setStockTrends] = useState([]);
  const [stockByCategory, setStockByCategory] = useState([]);
  const [topValueProducts, setTopValueProducts] = useState([]);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [realStockMovements, setRealStockMovements] = useState([]);
  const [stockValuationFromAPI, setStockValuationFromAPI] = useState(null);
  
  // Use passed props instead of context calls for better performance
  const { 
    products: contextProducts, 
    sales, 
    customers: contextCustomers, 
    purchases 
  } = useAppData();
  const { documents: contextDocuments } = useDocuments();
  const { companySettings } = useCompanySettings();
  
  // Use props if available, otherwise fallback to context
  const finalProducts = products || contextProducts;
  const finalCustomers = customers || contextCustomers;
  const finalDocuments = documents || contextDocuments;
  
  // Debugging: Log available data - ENHANCED DEBUGGING
  console.log('🔍 InventoryReports Data:', {
    productsCount: finalProducts?.length || 0,
    documentsCount: finalDocuments?.length || 0,
    customersCount: finalCustomers?.length || 0,
    purchasesCount: purchases?.length || 0,
    productsSample: finalProducts?.[0],
    documentsSample: finalDocuments?.[0],
    purchasesSample: purchases?.[0]
  });

  // Types de rapports d'inventaire
  const reportTypes = [
    {
      id: 'stock-overview',
      name: 'Vue d\'ensemble du stock',
      description: 'Aperçu général des niveaux de stock actuels et des tendances.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'stock-movements',
      name: 'Mouvements de stock',
      description: 'Analyse des entrées et sorties de stock sur une période donnée.',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'low-stock',
      name: 'Stock faible',
      description: 'Produits avec des niveaux de stock critiques nécessitant un réapprovisionnement.',
      color: 'from-red-500 to-red-600'
    },
    {
      id: 'stock-valuation',
      name: 'Valorisation des stocks',
      description: 'Évaluation financière des stocks actuels avec analyse de rentabilité.',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  // Memoized stock data for recent stock overview
  const memoizedStockData = useMemo(() => {
    if (!finalProducts || finalProducts.length === 0) {
      console.log('⚠️ No products available for stock overview');
      return [];
    }

    console.log('📦 Calculating stock overview from products:', finalProducts.length);

    const stockOverview = finalProducts
      .map(product => ({
        id: product.id,
        name: product.name || 'Produit inconnu',
        category: product.category || 'Non catégorisé',
        currentStock: product.stock || 0,
        minStock: product.minStock || 5,
        maxStock: product.maxStock || 100,
        price: product.price || 0,
        cost: product.cost || 0,
        value: (product.stock || 0) * (product.price || 0),
        status: (product.stock || 0) <= (product.minStock || 5) ? 'Faible' : 
                (product.stock || 0) >= (product.maxStock || 100) ? 'Élevé' : 'Normal',
        lastUpdated: product.updatedAt || new Date().toISOString(),
        sku: product.sku || 'N/A'
      }))
      .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
      .slice(0, 10);

    console.log('📦 Stock overview calculated:', stockOverview);
    return stockOverview;
  }, [finalProducts]);

  // Generate stock movements data from purchases and sales
  const generateStockMovements = () => {
    console.log('🔄 Generating stock movements data from REAL API data', {
      realMovementsCount: realStockMovements?.length || 0,
      purchasesCount: purchases?.length || 0,
      salesCount: sales?.length || 0,
      documentsCount: finalDocuments?.length || 0
    });

    const movements = [];

    // PRIORITY: Use real stock movements from API first
    if (realStockMovements && realStockMovements.length > 0) {
      realStockMovements.forEach(movement => {
        movements.push({
          date: movement.date || movement.createdAt || new Date().toISOString(),
          product: movement.productName || 'Produit inconnu',
          type: movement.type === 'entrée' ? 'Entrée' : 
                movement.type === 'sortie' ? 'Sortie' : 
                movement.type === 'ajustement' ? 'Ajustement' : 
                movement.type === 'transfert' ? 'Transfert' : 'Autre',
          quantity: movement.type === 'sortie' ? -(movement.quantity || 0) : (movement.quantity || 0),
          reference: movement.reference || movement.id || 'N/A',
          source: 'Stock API',
          value: (movement.quantity || 0) * (finalProducts?.find(p => p.id === movement.productId)?.price || 0),
          location: movement.location || 'N/A',
          notes: movement.notes || '',
          user: movement.user || 'Système'
        });
      });
    }

    // FALLBACK: Add movements from purchases (stock IN) - only if no real movements available
    if (purchases && purchases.length > 0) {
      purchases.forEach(purchase => {
        if (purchase.items && Array.isArray(purchase.items)) {
          purchase.items.forEach(item => {
            movements.push({
              date: purchase.date || purchase.createdAt || new Date().toISOString(),
              product: item.name || item.description || 'Produit inconnu',
              type: 'Entrée',
              quantity: parseInt(item.quantity) || 0,
              reference: purchase.reference || purchase.number || 'N/A',
              source: 'Achat',
              value: (parseInt(item.quantity) || 0) * (parseFloat(item.price) || 0)
            });
          });
        }
      });
    }

    // Add movements from sales (stock OUT)
    if (sales && sales.length > 0) {
      sales.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            movements.push({
              date: sale.date || sale.createdAt || new Date().toISOString(),
              product: item.name || item.productName || 'Produit inconnu',
              type: 'Sortie',
              quantity: -(parseInt(item.quantity) || 0), // Negative for stock out
              reference: sale.reference || sale.invoiceNumber || 'N/A',
              source: 'Vente',
              value: (parseInt(item.quantity) || 0) * (parseFloat(item.price) || 0)
            });
          });
        }
      });
    }

    // Add movements from documents (deliveries, receptions)
    if (finalDocuments && finalDocuments.length > 0) {
      finalDocuments.forEach(doc => {
        if (['delivery-note', 'reception-note', 'purchase-order'].includes(doc.type) && doc.items) {
          doc.items.forEach(item => {
            movements.push({
              date: doc.date || doc.createdAt || new Date().toISOString(),
              product: item.description || item.name || 'Produit inconnu',
              type: doc.type === 'reception-note' ? 'Entrée' : 'Sortie',
              quantity: doc.type === 'reception-note' ? 
                (parseInt(item.quantity) || 0) : 
                -(parseInt(item.quantity) || 0),
              reference: doc.number || doc.reference || 'N/A',
              source: 'Document',
              value: (parseInt(item.quantity) || 0) * (parseFloat(item.price) || 0)
            });
          });
        }
      });
    }

    // Sort by date (most recent first) and limit to recent movements
    const sortedMovements = movements
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 20);

    console.log('🔄 Stock movements generated:', sortedMovements.length, 'movements');
    return sortedMovements;
  };

  // Generate low stock alerts
  const generateLowStockData = () => {
    if (!finalProducts || finalProducts.length === 0) return [];

    console.log('⚠️ Generating low stock alerts');

    const lowStockProducts = finalProducts
      .filter(product => {
        const currentStock = product.stock || 0;
        const minStock = product.minStock || 5;
        return currentStock <= minStock;
      })
      .map(product => ({
        id: product.id,
        name: product.name || 'Produit inconnu',
        category: product.category || 'Non catégorisé',
        currentStock: product.stock || 0,
        minStock: product.minStock || 5,
        needed: Math.max((product.minStock || 5) - (product.stock || 0), 0),
        priority: (product.stock || 0) === 0 ? 'Critique' : 
                 (product.stock || 0) <= 2 ? 'Urgent' : 'Normal',
        value: (product.stock || 0) * (product.price || 0),
        lastOrder: 'N/A' // Could be enhanced with order history
      }))
      .sort((a, b) => a.currentStock - b.currentStock); // Most critical first

    console.log('⚠️ Low stock alerts generated:', lowStockProducts.length, 'products');
    return lowStockProducts;
  };

  // Generate stock valuation data
  const generateStockValuation = () => {
    if (!finalProducts || finalProducts.length === 0) return [];

    console.log('💰 Generating stock valuation');

    const valuation = finalProducts
      .map(product => {
        const currentStock = product.stock || 0;
        const costPrice = product.cost || 0;
        const sellPrice = product.price || 0;
        const stockValue = currentStock * costPrice;
        const marketValue = currentStock * sellPrice;
        const potentialProfit = marketValue - stockValue;
        const margin = stockValue > 0 ? ((potentialProfit / stockValue) * 100) : 0;

        return {
          id: product.id,
          name: product.name || 'Produit inconnu',
          category: product.category || 'Non catégorisé',
          quantity: currentStock,
          costPrice: costPrice,
          sellPrice: sellPrice,
          stockValue: stockValue,
          marketValue: marketValue,
          potentialProfit: potentialProfit,
          margin: margin.toFixed(1),
          turnover: 'N/A' // Could be calculated with sales velocity
        };
      })
      .filter(item => item.quantity > 0)
      .sort((a, b) => b.stockValue - a.stockValue); // Highest value first

    console.log('💰 Stock valuation generated:', valuation.length, 'products');
    return valuation;
  };

  // Generate PURE real-time stock trends using ONLY actual stock movements
  const generateStockTrends = () => {
    console.log('📈 Generating PURE REAL-TIME stock trends from movements:', realStockMovements.length);

    const currentDate = new Date();
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const currentYear = currentDate.getFullYear();

    // If no real stock movements, return empty data - NO FALLBACKS!
    if (!realStockMovements || realStockMovements.length === 0) {
      console.log('⚠️ No real stock movements found - returning empty chart data');
      return months.map(month => ({
        name: month,
        stockValue: 0,
        itemCount: 0,
        lowStockCount: 0,
        movements: 0,
        netChange: 0,
        entriesValue: 0,
        exitsValue: 0
      }));
    }

    const trends = months.map((month, idx) => {
      // Calculate stock movements for this month ONLY
      const monthMovements = realStockMovements.filter(movement => {
        const movementDate = new Date(movement.date || movement.createdAt);
        return movementDate.getFullYear() === currentYear && movementDate.getMonth() === idx;
      });

      // Calculate ONLY the actual movements value for this month
      let entriesValue = 0;
      let exitsValue = 0;
      let netStockChange = 0;

      monthMovements.forEach(movement => {
        const product = finalProducts?.find(p => p.id === movement.productId);
        const productPrice = product?.price || 0;
        const quantity = movement.quantity || 0;
        const value = quantity * productPrice;

        if (movement.type === 'entrée') {
          entriesValue += value;
          netStockChange += quantity;
        } else if (movement.type === 'sortie') {
          exitsValue += value;
          netStockChange -= quantity;
        } else if (movement.type === 'ajustement') {
          // For adjustments, calculate the value impact
          entriesValue += value;
          netStockChange += quantity;
        } else if (movement.type === 'transfert') {
          // Transfers are neutral but show activity
          netStockChange += 0;
        }
      });

      // Stock value is ONLY based on actual movements in this month
      const monthStockValue = entriesValue - exitsValue;

      return {
        name: month,
        stockValue: Math.max(0, Math.round(monthStockValue)), // ONLY real movement value
        itemCount: monthMovements.length, // Number of movements, not products
        lowStockCount: 0, // Not relevant for movement-based chart
        movements: monthMovements.length,
        netChange: netStockChange,
        entriesValue: Math.round(entriesValue),
        exitsValue: Math.round(exitsValue)
      };
    });

    console.log('📈 PURE REAL-TIME Stock trends generated:', trends);
    console.log('📊 Chart will show ONLY actual stock movement values per month');
    return trends;
  };

  // Generate stock by category data
  const generateStockByCategory = () => {
    if (!finalProducts || finalProducts.length === 0) {
      console.log('⚠️ No products available for stock by category');
      return [];
    }

    console.log('🏷️ Generating stock by category from products:', {
      totalProducts: finalProducts.length,
      sampleProducts: finalProducts.slice(0, 3),
      allProducts: finalProducts
    });

    const categoryData = {};

    finalProducts.forEach(product => {
      const category = product.category || 'Non catégorisé';
      const quantity = product.stock || 0;
      const value = quantity * (product.price || 0);

      if (!categoryData[category]) {
        categoryData[category] = {
          name: category,
          quantity: 0,
          value: 0,
          products: 0
        };
      }

      categoryData[category].quantity += quantity;
      categoryData[category].value += value;
      categoryData[category].products += 1;
    });

    const result = Object.values(categoryData)
      .sort((a, b) => b.value - a.value);

    console.log('🏷️ Stock by category generated:', result);
    return result;
  };

  // Generate top value products
  const generateTopValueProducts = () => {
    if (!finalProducts || finalProducts.length === 0) return [];

    console.log('💎 Generating top value products');

    const topProducts = finalProducts
      .map(product => ({
        id: product.id,
        name: product.name || 'Produit inconnu',
        category: product.category || 'Non catégorisé',
      quantity: product.stock || 0,
        value: (product.stock || 0) * (product.price || 0),
        price: product.price || 0
      }))
      .filter(product => product.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    console.log('💎 Top value products generated:', topProducts);
    return topProducts;
  };

  // Load real stock data from API
  const loadRealStockData = async () => {
    try {
      console.log('🔄 Loading real stock data from API...');
      
      // Load stock movements
      const movements = await stockService.getStockMovements();
      setRealStockMovements(movements);
      console.log('📦 Loaded stock movements:', movements.length);
      
      // Load stock valuation
      const valuation = await stockService.getStockValuation();
      setStockValuationFromAPI(valuation);
      console.log('💰 Loaded stock valuation:', valuation);
      
      return { movements, valuation };
    } catch (error) {
      console.error('❌ Error loading real stock data:', error);
      toast.error('Erreur lors du chargement des données de stock');
      return { movements: [], valuation: null };
    }
  };

  // useEffect to calculate all data when dependencies change
  useEffect(() => {
    console.log('📊 InventoryReports useEffect triggered', {
      productsLength: finalProducts?.length || 0,
      purchasesLength: purchases?.length || 0,
      documentsLength: finalDocuments?.length || 0,
      trigger: 'data change'
    });
    
    if (finalProducts && finalProducts.length > 0) {
      console.log('🔍 DEBUG Products Structure:', {
        totalProducts: finalProducts.length,
        firstProduct: finalProducts[0],
        productsWithStock: finalProducts.filter(p => (p.stock || 0) > 0).length,
        lowStockProducts: finalProducts.filter(p => (p.stock || 0) <= (p.minStock || 5)).length
      });
    }

    // Load real stock data first, then generate reports
    const initializeData = async () => {
      await loadRealStockData();
      
      // Generate all inventory data (now with real stock movements)
      setStockData(memoizedStockData);
      setStockMovementsData(generateStockMovements());
      setLowStockData(generateLowStockData());
      setStockValuationData(generateStockValuation());
      setStockTrends(generateStockTrends());
      setStockByCategory(generateStockByCategory());
      setTopValueProducts(generateTopValueProducts());
    };
    
    initializeData();
    
  }, [finalProducts, finalDocuments, purchases, sales, periodFilter, memoizedStockData, dataRefreshKey]);

  // Force refresh handler
  const handleForceRefresh = async () => {
    console.log('🔄 Force refreshing inventory data...');
    
    console.log('🔍 CURRENT DATA STATE:', {
      products: finalProducts?.length || 0,
      stockData: stockData?.length || 0,
      movements: stockMovementsData?.length || 0,
      lowStock: lowStockData?.length || 0,
      valuation: stockValuationData?.length || 0,
      trends: stockTrends?.length || 0,
      categories: stockByCategory?.length || 0,
      topValue: topValueProducts?.length || 0,
      realMovements: realStockMovements?.length || 0
    });
    
    if (finalProducts && finalProducts.length > 0) {
      console.log('🔍 PRODUCTS SAMPLE:', finalProducts[0]);
    }
    
    // Reload real stock data
    const { movements, valuation } = await loadRealStockData();
    
    if (!movements || movements.length === 0) {
      toast.warning('Aucun mouvement de stock trouvé! Le graphique affichera des données vides.');
    } else {
      toast.success(`Données actualisées avec ${movements.length} mouvements de stock réels!`);
    }
    
    setDataRefreshKey(prev => prev + 1);
  };

  // Comprehensive PDF export function
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting Inventory PDF generation...');
      console.log('📊 Data check:', {
        stockData: stockData?.length || 0,
        movements: stockMovementsData?.length || 0,
        lowStock: lowStockData?.length || 0,
        valuation: stockValuationData?.length || 0,
        trends: stockTrends?.length || 0,
        categories: stockByCategory?.length || 0,
        topValue: topValueProducts?.length || 0
      });
      
      // Minimalist red theme colors
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            // Create image from logo data (base64)
            const logoImg = new Image();
            logoImg.src = companySettings.logo;
            
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header
      const addSectionHeader = async (title) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPosition = 30; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPosition);
        
        // Thin underline
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.1);
        const textWidth = pdf.getTextWidth(title);
        pdf.line(20, yPosition + 2, 20 + textWidth, yPosition + 2);
        
        yPosition += 8; // Add space after header
      };
      
      // Start first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // Main Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT D\'INVENTAIRE', 20, yPosition);
      yPosition += 8;
      
      // Date and time
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 20;
      
      // Executive Summary
      await addSectionHeader('RÉSUMÉ EXÉCUTIF');
      
      const totalProducts = finalProducts?.length || 0;
      const totalStockValue = finalProducts?.reduce((sum, p) => sum + ((p.stock || 0) * (p.price || 0)), 0) || 0;
      const lowStockCount = finalProducts?.filter(p => (p.stock || 0) <= (p.minStock || 5)).length || 0;
      const averageStockValue = totalProducts > 0 ? totalStockValue / totalProducts : 0;
      
      const summaryData = [
        ['Total Produits', totalProducts.toString()],
        ['Valeur Total Stock', `${totalStockValue.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`],
        ['Produits en Stock Faible', lowStockCount.toString()],
        ['Valeur Moyenne par Produit', `${averageStockValue.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`],
        ['Catégories', stockByCategory?.length?.toString() || '0']
      ];
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Métrique', 'Valeur']],
        body: summaryData,
        theme: 'plain',
        headStyles: { fillColor: primaryRed, textColor: 255, fontSize: 9, halign: 'left' },
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.1 },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Stock Overview
      await addSectionHeader('APERÇU DU STOCK');
      
      if (stockData && stockData.length > 0) {
        const stockOverviewData = stockData.slice(0, 8).map((item, index) => [
          (index + 1).toString(),
          item.name || 'N/A',
          item.category || 'N/A',
          item.currentStock?.toString() || '0',
          `${item.value?.toLocaleString('fr-FR').replace(/\s/g, ',')} DA` || '0 DA',
          item.status || 'N/A'
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['#', 'Produit', 'Catégorie', 'Stock', 'Valeur', 'Statut']],
          body: stockOverviewData,
          theme: 'plain',
          headStyles: { fillColor: primaryRed, textColor: 255, fontSize: 8, halign: 'left' },
          styles: { fontSize: 7, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.1 },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      } else {
        pdf.setFontSize(10);
        pdf.setTextColor(120, 120, 120);
        pdf.text('Aucun produit en stock disponible.', 20, yPosition);
        yPosition += 15;
      }
      
      // Low Stock Alerts
      if (lowStockData && lowStockData.length > 0) {
        await addSectionHeader('ALERTES STOCK FAIBLE');
        
        const lowStockTableData = lowStockData.slice(0, 8).map((item, index) => [
          (index + 1).toString(),
          item.name || 'N/A',
          item.currentStock?.toString() || '0',
          item.minStock?.toString() || '0',
          item.needed?.toString() || '0',
          item.priority || 'N/A'
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['#', 'Produit', 'Stock Actuel', 'Stock Min', 'Besoin', 'Priorité']],
          body: lowStockTableData,
          theme: 'plain',
          headStyles: { fillColor: primaryRed, textColor: 255, fontSize: 8, halign: 'left' },
          styles: { fontSize: 7, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.1 },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // Top Value Products
      if (topValueProducts && topValueProducts.length > 0) {
        await addSectionHeader('PRODUITS À PLUS FORTE VALEUR');
        
        const topValueTableData = topValueProducts.slice(0, 8).map((item, index) => [
          (index + 1).toString(),
          item.name || 'N/A',
          item.category || 'N/A',
          item.quantity?.toString() || '0',
          `${item.value?.toLocaleString('fr-FR')} DA` || '0 DA'
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Rang', 'Produit', 'Catégorie', 'Quantité', 'Valeur Totale']],
          body: topValueTableData,
          theme: 'plain',
          headStyles: { fillColor: primaryRed, textColor: 255, fontSize: 8, halign: 'left' },
          styles: { fontSize: 7, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.1 },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // Stock by Category
      if (stockByCategory && stockByCategory.length > 0) {
        await addSectionHeader('STOCK PAR CATÉGORIE');
        
        const categoryTableData = stockByCategory.slice(0, 8).map((item, index) => [
          (index + 1).toString(),
          item.name || 'N/A',
          item.products?.toString() || '0',
          item.quantity?.toString() || '0',
          `${item.value?.toLocaleString('fr-FR')} DA` || '0 DA'
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['#', 'Catégorie', 'Nb. Produits', 'Quantité Totale', 'Valeur Totale']],
          body: categoryTableData,
          theme: 'plain',
          headStyles: { fillColor: primaryRed, textColor: 255, fontSize: 8, halign: 'left' },
          styles: { fontSize: 7, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.1 },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // Final page setup (add header/footer to all pages)
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      const fileName = `Rapport_Inventaire_Complet_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving Inventory PDF:', fileName);
      
      pdf.save(fileName);
      
      setIsGenerating(false);
      toast.success('Rapport d\'inventaire PDF complet généré avec succès!');
      console.log('✅ Inventory PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ Inventory PDF generation error:', error);
      setIsGenerating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Rendu du contenu du rapport avec charts et données temps réel
  const renderReportContent = () => {
    switch (selectedReport) {
      case 'stock-overview':
        return (
          <div className="space-y-6">
            {/* Aperçu du stock avec graphique */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-white">Niveaux de stock actuels</h4>
                <button
                  onClick={handleForceRefresh}
                  className="p-1 text-white/60 hover:text-white transition-colors"
                  title="Actualiser les données"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="h-80">
                {stockTrends && stockTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stockTrends}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                    <YAxis stroke="rgba(255,255,255,0.6)" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: 'white'
                      }}
                      formatter={(value, name) => [
                        name === 'stockValue' ? `${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA` : value,
                        name === 'stockValue' ? 'Valeur du stock' : 
                        name === 'itemCount' ? 'Nombre d\'articles' : 'Stock faible'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="stockValue" fill="#82ca9d" name="Valeur du stock" />
                    <Bar dataKey="lowStockCount" fill="#ff7c7c" name="Stock faible" />
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/60">
                    <ChartBarIcon className="h-16 w-16 mb-4 opacity-50" />
                    <p>Aucune donnée de stock</p>
                    <p className="text-sm">Ajoutez des produits pour voir les tendances</p>
                  </div>
                )}
              </div>
            </div>

            {/* Graphique en secteurs des stocks par catégorie */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Répartition par catégorie</h4>
              <div className="h-80">
                {stockByCategory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stockByCategory}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stockByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                          borderColor: 'rgba(255,255,255,0.2)',
                          color: 'white'
                        }}
                                                    formatter={(value) => [`${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`, 'Valeur']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/60">
                    <ChartPieIcon className="h-16 w-16 mb-4 opacity-50" />
                    <p>Aucune catégorie disponible</p>
                    <p className="text-sm">Organisez vos produits par catégorie</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tableau récent du stock */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Stock récent</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/80 font-medium py-2">Produit</th>
                      <th className="text-left text-white/80 font-medium py-2">Catégorie</th>
                      <th className="text-left text-white/80 font-medium py-2">Stock</th>
                      <th className="text-left text-white/80 font-medium py-2">Valeur</th>
                      <th className="text-left text-white/80 font-medium py-2">Statut</th>
                      <th className="text-left text-white/80 font-medium py-2">Dernière MAJ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {stockData.length > 0 ? stockData.map((item, index) => (
                      <tr key={index} className="hover:bg-white/5">
                        <td className="py-3 text-white">{item.name}</td>
                        <td className="py-3 text-white/70">{item.category}</td>
                        <td className="py-3 text-white">{item.currentStock}</td>
                        <td className="py-3 text-white">{item.value?.toLocaleString('fr-FR').replace(/\s/g, ',')} DA</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.status === 'Faible' ? 'bg-red-500/20 text-red-300' :
                            item.status === 'Élevé' ? 'bg-blue-500/20 text-blue-300' :
                            'bg-green-500/20 text-green-300'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="py-3 text-white/70">{new Date(item.lastUpdated).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-white/60">
                          <ArchiveBoxIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Aucune donnée de stock disponible</p>
                          <p className="text-sm">Ajoutez des produits pour voir l'aperçu</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'stock-movements':
        return (
          <div className="space-y-6">
            {/* Graphique des mouvements de stock */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Tendances des mouvements</h4>
              <div className="h-80">
                {stockMovementsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stockMovementsData.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="product" stroke="rgba(255,255,255,0.6)" />
                      <YAxis stroke="rgba(255,255,255,0.6)" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                          borderColor: 'rgba(255,255,255,0.2)',
                          color: 'white'
                        }}
                      />
                      <Line type="monotone" dataKey="quantity" stroke="#82ca9d" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/60">
                    <ArrowTrendingUpIcon className="h-16 w-16 mb-4 opacity-50" />
                    <p>Aucun mouvement de stock</p>
                    <p className="text-sm">Les mouvements apparaîtront ici</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tableau des mouvements récents */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Mouvements récents</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/80 font-medium py-2">Date</th>
                      <th className="text-left text-white/80 font-medium py-2">Produit</th>
                      <th className="text-left text-white/80 font-medium py-2">Type</th>
                      <th className="text-left text-white/80 font-medium py-2">Quantité</th>
                      <th className="text-left text-white/80 font-medium py-2">Référence</th>
                      <th className="text-left text-white/80 font-medium py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {stockMovementsData.length > 0 ? stockMovementsData.slice(0, 10).map((movement, index) => (
                      <tr key={index} className="hover:bg-white/5">
                        <td className="py-3 text-white/70">{new Date(movement.date).toLocaleDateString('fr-FR')}</td>
                        <td className="py-3 text-white">{movement.product}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            movement.type === 'Entrée' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                          }`}>
                            {movement.type}
                          </span>
                        </td>
                        <td className="py-3 text-white">{Math.abs(movement.quantity)}</td>
                        <td className="py-3 text-white/70">{movement.reference}</td>
                        <td className="py-3 text-white/70">{movement.source}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-white/60">
                          <ArrowTrendingUpIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Aucun mouvement de stock</p>
                          <p className="text-sm">Les transactions apparaîtront ici</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'low-stock':
        return (
          <div className="space-y-6">
            {/* Alertes stock faible */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                Alertes de stock faible
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/80 font-medium py-2">Produit</th>
                      <th className="text-left text-white/80 font-medium py-2">Catégorie</th>
                      <th className="text-left text-white/80 font-medium py-2">Stock Actuel</th>
                      <th className="text-left text-white/80 font-medium py-2">Stock Min</th>
                      <th className="text-left text-white/80 font-medium py-2">Besoin</th>
                      <th className="text-left text-white/80 font-medium py-2">Priorité</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {lowStockData.length > 0 ? lowStockData.map((item, index) => (
                      <tr key={index} className="hover:bg-white/5">
                        <td className="py-3 text-white">{item.name}</td>
                        <td className="py-3 text-white/70">{item.category}</td>
                        <td className="py-3 text-white">{item.currentStock}</td>
                        <td className="py-3 text-white/70">{item.minStock}</td>
                        <td className="py-3 text-white">{item.needed}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.priority === 'Critique' ? 'bg-red-600/30 text-red-300' :
                            item.priority === 'Urgent' ? 'bg-orange-500/30 text-orange-300' :
                            'bg-yellow-500/30 text-yellow-300'
                          }`}>
                            {item.priority}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-white/60">
                          <ArchiveBoxIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Aucun produit en stock faible</p>
                          <p className="text-sm">Tous vos produits ont des niveaux normaux</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions recommandées */}
            {lowStockData.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <h4 className="text-lg font-medium text-red-300 mb-2">Actions recommandées</h4>
                <ul className="space-y-2 text-white/70">
                  <li>• Réapprovisionner les produits critiques immédiatement</li>
                  <li>• Contacter les fournisseurs pour les produits urgents</li>
                  <li>• Réviser les seuils de stock minimum si nécessaire</li>
                  <li>• Configurer des alertes automatiques</li>
                </ul>
              </div>
            )}
          </div>
        );

      case 'stock-valuation':
        return (
          <div className="space-y-6">
            {/* Graphique de valorisation */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Produits les plus valorisés</h4>
              <div className="h-80">
                {topValueProducts.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topValueProducts}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis type="number" stroke="rgba(255,255,255,0.6)" />
                      <YAxis dataKey="name" type="category" width={150} stroke="rgba(255,255,255,0.6)" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                          borderColor: 'rgba(255,255,255,0.2)',
                          color: 'white'
                        }}
                                                    formatter={(value) => [`${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`, 'Valeur']}
                      />
                      <Bar dataKey="value" fill="#82ca9d" name="Valeur du stock" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/60">
                    <CurrencyDollarIcon className="h-16 w-16 mb-4 opacity-50" />
                    <p>Aucune valorisation disponible</p>
                    <p className="text-sm">Ajoutez des prix aux produits</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tableau de valorisation */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Valorisation détaillée</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/80 font-medium py-2">Produit</th>
                      <th className="text-left text-white/80 font-medium py-2">Quantité</th>
                      <th className="text-left text-white/80 font-medium py-2">Prix Coût</th>
                      <th className="text-left text-white/80 font-medium py-2">Prix Vente</th>
                      <th className="text-left text-white/80 font-medium py-2">Valeur Stock</th>
                      <th className="text-left text-white/80 font-medium py-2">Valeur Marché</th>
                      <th className="text-left text-white/80 font-medium py-2">Marge</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {stockValuationData.length > 0 ? stockValuationData.slice(0, 10).map((item, index) => (
                      <tr key={index} className="hover:bg-white/5">
                        <td className="py-3 text-white">{item.name}</td>
                        <td className="py-3 text-white">{item.quantity}</td>
                        <td className="py-3 text-white/70">{item.costPrice.toLocaleString('fr-FR').replace(/\s/g, ',')} DA</td>
                        <td className="py-3 text-white/70">{item.sellPrice.toLocaleString('fr-FR').replace(/\s/g, ',')} DA</td>
                        <td className="py-3 text-white">{item.stockValue.toLocaleString('fr-FR').replace(/\s/g, ',')} DA</td>
                        <td className="py-3 text-white">{item.marketValue.toLocaleString('fr-FR').replace(/\s/g, ',')} DA</td>
                        <td className="py-3">
                          <span className={`font-medium ${
                            parseFloat(item.margin) > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {item.margin}%
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-white/60">
                          <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Aucune donnée de valorisation</p>
                          <p className="text-sm">Configurez les prix pour voir la valorisation</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with back button and PDF export */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Rapports d'inventaire</h1>
          <p className="text-white/60 text-sm mt-1">
            Analysez vos niveaux de stock et vos mouvements d'inventaire
          </p>
        </div>
      </div>

        {/* Master PDF Export Button for entire Inventory section */}
                    <button
                      onClick={handleGenerateReport}
                      disabled={isGenerating}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium transition-colors disabled:opacity-50 shadow-lg"
                    >
          <DocumentTextIcon className="h-5 w-5" />
          <span>{isGenerating ? 'Génération...' : 'Exporter PDF Complet'}</span>
                    </button>
                </div>
                
      {/* Navigation tabs */}
      <div className="flex space-x-1 bg-white/10 p-1 rounded-xl">
        {reportTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setSelectedReport(type.id)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedReport === type.id
                ? 'bg-white text-gray-900'
                : 'text-white hover:bg-white/10'
            }`}
          >
            {type.name}
          </button>
        ))}
                </div>
                
      {/* Report content */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
        {renderReportContent()}
      </div>
    </motion.div>
  );
};

export default InventoryReports; 